/**
 * InflowPayProvider - Compatibility layer for React SDK API
 * 
 * Provides the same API structure as the original React SDK
 * but works with vanilla JavaScript
 */

import type { CardElement, CardElementOptions } from './card-element';
import type { PaymentSDKConfig } from './payment-sdk';
import { PaymentSDK } from './payment-sdk';
import { PaymentResult } from './react';
import type { Locale } from './types';

export interface InflowPayProviderConfig {
  publicKey: string;
  locale?: Locale;
}

export interface CardElementProps {
  paymentId: string;
  container?: string | HTMLElement;
  onComplete?: (result: PaymentResult) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
  onReady?: () => void;
  onChange?: (state: { complete: boolean }) => void;
  buttonText?: string;
  buttonStyle?: any;
  style?: any;
  placeholders?: {
    cardNumber?: string;
    expiry?: string;
    cvc?: string;
  };
}

/**
 * InflowPayProvider - Global SDK configuration
 * 
 * Similar to React's InflowPayProvider but for vanilla JS
 * 
 * @example
 * ```typescript
 * const provider = new InflowPayProvider({
 *   config: { apiKey: 'inflow_pub_xxx' }
 * });
 * 
 * const cardElement = provider.createCardElement({
 *   paymentId: 'pay_xxx',
 *   onComplete: (result) => {
 *     if (result.status === 'CHECKOUT_SUCCESS') {
 *       window.location.href = '/success';
 *     }
 *   }
 * });
 * 
 * cardElement.mount();
 * ```
 */
export class InflowPayProvider {
  private sdk: PaymentSDK;

  constructor(options: { config: InflowPayProviderConfig }) {
    const config: PaymentSDKConfig = {
      publicKey: options.config.publicKey,
      locale: options.config.locale,
    };

    this.sdk = new PaymentSDK(config);
  }

  /**
   * Create a CardElement (similar to React's <CardElement />)
   * 
   * @param props - CardElement props (same as React SDK)
   * @returns CardElement instance
   */
  createCardElement(props: CardElementProps): CardElement {
    let container: string | HTMLElement;
    if (props.container) {
      container = props.container;
    } else {
      const defaultContainer = document.createElement('div');
      defaultContainer.id = 'inflowpay-card-element-container';
      document.body.appendChild(defaultContainer);
      container = defaultContainer;
    }

    const cardElementOptions: CardElementOptions = {
      container: container,
      paymentId: props.paymentId,
      ...(props.style && { style: props.style }),
      ...(props.buttonText && { buttonText: props.buttonText }),
      ...(props.placeholders && { placeholders: props.placeholders }),
      onComplete: (result) => {
        if (props.onComplete) {
          props.onComplete(result);
        }
      },
      onError: props.onError,
      onClose: props.onClose,
    };

    const cardElement = this.sdk.createCardElement(cardElementOptions);

    if (props.onReady) {
      const originalMount = cardElement.mount.bind(cardElement);
      cardElement.mount = () => {
        originalMount();
        setTimeout(() => {
          if (props.onReady) {
            props.onReady();
          }
        }, 100);
      };
    }

    if (props.onChange) {
      setTimeout(() => {
        if (props.onChange) {
          props.onChange({ complete: false });
        }
      }, 100);
    }

    return cardElement;
  }

  /**
   * Get the underlying PaymentSDK instance
   */
  getSDK(): PaymentSDK {
    return this.sdk;
  }
}

